import { Alert, Spin } from 'antd';
import { Profile } from 'oidc-client';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import Routes from '../../config/routes';
import { defaultSelectedUserRole, getUserRole } from '../../helpers/CheckPermissionHelper';
import { MatchVm } from '../../utils/api';
import './insights.css';
import { canAccessInsights, canUseInsightsPages } from './insightsAccess';
import { useInsightsClub } from './insightsClubScope';
import { isFinishedMatch, ourTeamIds, perGame } from './insightsClubSummary';
import {
    buildGameStateSplits,
    matchPlayerIdsForPlayer,
    mergeGameStateSplits,
    shootPct,
} from './insightsGameState';
import InsightsGkPlayerMaps from './InsightsGkPlayerMaps';
import { collectGkZoneStatsForPlayer, savePctText } from './insightsGoalkeeperZones';
import InsightsLegend from './InsightsLegend';
import {
    collectBundleFilterOptions,
    emptyMatchBundleFilters,
    filterMatchBundles,
    MatchBundleFilterSelection,
} from './insightsMatchBundleFilters';
import InsightsNav from './InsightsNav';
import {
    buildConsistencyStats,
    buildPlayerTrendSeries,
    buildTwFieldFormComparison,
    buildTwGkFormComparison,
    FIELD_TREND_METRICS,
    formatFormDelta,
    formatFormValue,
    formDeltaClass,
    GK_TREND_METRICS,
    PlayerTrendMetric,
} from './insightsPlayerForm';
import InsightsPlayerShotMaps from './InsightsPlayerShotMaps';
import { countPlayerShots, resolvePlayerMapRole } from './insightsPlayerShotZones';
import {
    buildAssistThresholdRecord,
    buildFoulTroubleSummary,
    buildSampleHalfComparison,
    buildVenuePlayerSplits,
    buildWinLossSplits,
    ResultSplitRow,
    SampleHalfComparison,
} from './insightsPlayerSplits';
import InsightsPlayerTrendChart from './InsightsPlayerTrendChart';
import { fetchClubMatches, loadMatchBundles, MatchBundle } from './insightsQueries';
import InsightsScopeFilters from './InsightsScopeFilters';
import InsightsTopbar from './InsightsTopbar';
import {
    TwMatchSavesBoard,
    TwMatchScorersBoard,
    TwMatchSupportExclusionsBoard,
} from './InsightsTwBoards';
import {
    buildTwPlayerBoards,
    buildTwPlayerMatchLines,
    shotPctText,
    TW_SIT_KEYS,
    TW_SIT_LABELS,
    TwPlayerBoardRow,
    TwSitKey,
} from './insightsTwPlayerTables';

interface RouteParams {
    playerId: string;
}

interface Props extends RouteComponentProps<RouteParams> {
    userProfile?: Profile;
}

const PAGE_SIZE = 100;

type RateMode = 'totals' | 'perGame';

const InsightsPlayer: React.FC<Props> = ({ userProfile, match: routeMatch }) => {
    // getUserRole returns a new object every call; putting it in effect deps
    // retriggered the page fetch in a tight loop.
    const userRole = useMemo(
        () => (userProfile ? getUserRole(userProfile) : defaultSelectedUserRole),
        [userProfile]
    );
    const allowed = canAccessInsights(userRole);
    const playerId = parseInt(routeMatch.params.playerId, 10);

    const scope = useInsightsClub(userProfile, allowed && canUseInsightsPages(userRole));
    const { selectedClubId, selectedTeamId } = scope;

    const [allBundles, setAllBundles] = useState<MatchBundle[]>([]);
    const [filters, setFilters] = useState<MatchBundleFilterSelection>(emptyMatchBundleFilters());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [rateMode, setRateMode] = useState<RateMode>('totals');
    const [trendMetric, setTrendMetric] = useState<PlayerTrendMetric>('goals');

    const teamIds = useMemo(
        () => ourTeamIds(scope.teams, selectedTeamId),
        [scope.teams, selectedTeamId]
    );

    useEffect(() => {
        let cancelled = false;

        const load = async (): Promise<void> => {
            if (
                !allowed ||
                !canUseInsightsPages(userRole) ||
                Number.isNaN(playerId) ||
                selectedClubId == null ||
                teamIds.length === 0
            ) {
                setAllBundles([]);
                return;
            }

            setLoading(true);
            setError(undefined);
            try {
                const page = await fetchClubMatches({
                    clubId: selectedClubId,
                    teamId: selectedTeamId,
                    pageSize: PAGE_SIZE,
                });
                if (cancelled) return;

                const finished = (page.pageItems || []).filter((match: MatchVm) =>
                    isFinishedMatch(match)
                );
                // ponytail: loads club finished set then filters client-side. Ceiling is
                // PAGE_SIZE; upgrade path is server season aggregate.
                const bundles = await loadMatchBundles(finished);
                if (cancelled) return;

                setAllBundles(bundles);
                setFilters(emptyMatchBundleFilters());
            } catch {
                if (!cancelled) {
                    setAllBundles([]);
                    setError('Could not load this player.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [allowed, userRole, playerId, selectedClubId, selectedTeamId, teamIds]);

    const filterOptions = useMemo(
        () => collectBundleFilterOptions(allBundles, teamIds),
        [allBundles, teamIds]
    );

    const filteredBundles = useMemo(
        () => filterMatchBundles(allBundles, filters),
        [allBundles, filters]
    );

    const seasonRow: TwPlayerBoardRow | undefined = useMemo(() => {
        const rows = buildTwPlayerBoards(filteredBundles, teamIds);
        return rows.find((r) => r.playerId === playerId);
    }, [filteredBundles, teamIds, playerId]);

    const matchLines = useMemo(
        () => buildTwPlayerMatchLines(filteredBundles, teamIds, playerId),
        [filteredBundles, teamIds, playerId]
    );

    const mapRole = useMemo(() => {
        if (filteredBundles.length === 0) return { showGk: false, showShooter: false };
        const gkSog =
            collectGkZoneStatsForPlayer(filteredBundles, teamIds, playerId).keepers[0]?.sog ?? 0;
        const fieldShots = countPlayerShots(filteredBundles, teamIds, playerId);
        return resolvePlayerMapRole({ gkSog, fieldShots });
    }, [filteredBundles, teamIds, playerId]);

    const pureGk = mapRole.showGk && !mapRole.showShooter;
    const pureField = mapRole.showShooter && !mapRole.showGk;

    useEffect(() => {
        setTrendMetric(pureGk ? 'saves' : 'goals');
    }, [pureGk]);

    const gkTotals = useMemo(() => {
        if (!mapRole.showGk || !seasonRow) return undefined;
        return {
            saves: seasonRow.saves.total.saves,
            sog: seasonRow.saves.total.attempts,
            goalsAgainst: seasonRow.saves.total.attempts - seasonRow.saves.total.saves,
        };
    }, [mapRole.showGk, seasonRow]);

    const formRows = useMemo(() => {
        if (matchLines.length === 0) return [];
        if (pureGk) return buildTwGkFormComparison(matchLines);
        return buildTwFieldFormComparison(matchLines);
    }, [matchLines, pureGk]);

    const trendOptions = pureGk ? GK_TREND_METRICS : FIELD_TREND_METRICS;
    const trendSeries = useMemo(
        () => buildPlayerTrendSeries(matchLines, trendMetric),
        [matchLines, trendMetric]
    );
    const consistency = useMemo(
        () => buildConsistencyStats(matchLines, trendMetric),
        [matchLines, trendMetric]
    );

    const winLoss = useMemo(() => buildWinLossSplits(matchLines), [matchLines]);
    const venueSplits = useMemo(() => buildVenuePlayerSplits(matchLines), [matchLines]);
    const sampleHalves = useMemo(() => buildSampleHalfComparison(matchLines), [matchLines]);
    const assistRecord = useMemo(
        () => (pureGk ? undefined : buildAssistThresholdRecord(matchLines, 2)),
        [matchLines, pureGk]
    );
    const foulTrouble = useMemo(
        () => (pureGk ? undefined : buildFoulTroubleSummary(filteredBundles, teamIds, playerId)),
        [filteredBundles, teamIds, playerId, pureGk]
    );

    const situationSitKeys = useMemo(
        () => TW_SIT_KEYS.filter((k) => k !== 'total') as TwSitKey[],
        []
    );

    const playerGameState = useMemo(() => {
        if (filteredBundles.length === 0 || pureGk) return undefined;
        const parts = filteredBundles.map((bundle) =>
            buildGameStateSplits(
                bundle,
                teamIds,
                matchPlayerIdsForPlayer(bundle.detail, playerId, teamIds)
            )
        );
        return mergeGameStateSplits(parts);
    }, [filteredBundles, teamIds, playerId, pureGk]);

    const show = (total: number): string => {
        if (rateMode === 'totals' || !seasonRow || seasonRow.matches === 0) return `${total}`;
        return perGame(total, seasonRow.matches);
    };

    if (!canUseInsightsPages(userRole)) {
        return (
            <Alert
                type="warning"
                showIcon
                message="Insights is only on staging and the Statistics Portal"
            />
        );
    }

    if (!allowed) {
        return (
            <div className="insightsRoot">
                <InsightsTopbar />
                <div className="insightsBlocked">
                    <h1>Not available for this account</h1>
                    <p>Club Insights is for Federation and Club accounts that enter statistics.</p>
                </div>
            </div>
        );
    }

    const matchSavesLines = mapRole.showGk
        ? matchLines.filter((l) => l.board.saves.total.attempts > 0)
        : [];

    return (
        <div className="insightsRoot">
            <InsightsTopbar scope={scope}>
                <Link className="insightsBackLink" to={Routes.ROUTE_INSIGHTS_PLAYERS}>
                    All players
                </Link>
            </InsightsTopbar>

            <div className="insightsBody">
                <InsightsNav active="players" />
                <InsightsLegend />

                <InsightsScopeFilters
                    options={filterOptions}
                    filters={filters}
                    onChange={setFilters}
                    matchCount={filteredBundles.length}
                />

                <Spin spinning={loading || scope.loading}>
                    {error && (
                        <Alert type="error" showIcon message={error} style={{ marginBottom: 14 }} />
                    )}

                    {!loading && !seasonRow && !error && (
                        <div className="insightsEmpty">
                            {filteredBundles.length === 0
                                ? '0 matches for the current filters.'
                                : 'No lineup appearances for this player in the filtered matches.'}
                        </div>
                    )}

                    {seasonRow && (
                        <>
                            <div className="insightsHero">
                                <div>
                                    <h1>
                                        {seasonRow.capNumbers.length > 0
                                            ? `#${[...seasonRow.capNumbers]
                                                  .sort((a, b) => a - b)
                                                  .join('/')} `
                                            : ''}
                                        {seasonRow.name}
                                    </h1>
                                    <p>
                                        {pureGk
                                            ? 'Goalkeeper profile for the selected club, filtered matches.'
                                            : pureField
                                            ? 'Field player profile for the selected club, filtered matches.'
                                            : 'Player profile for the selected club, filtered matches.'}
                                    </p>
                                    {mapRole.showShooter ? (
                                        <div
                                            className="insightsStatStrip"
                                            style={{ marginTop: 12 }}
                                        >
                                            {situationSitKeys.map((sit) => {
                                                const bucket = seasonRow.shots[sit];
                                                return (
                                                    <div className="insightsStat" key={sit}>
                                                        <b>
                                                            {bucket.goals}/{bucket.attempts}
                                                        </b>
                                                        <i>{TW_SIT_LABELS[sit]}</i>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : null}
                                </div>
                            </div>

                            <section className="insightsPanel insightsFilteredTotals">
                                <div className="insightsPanelHead">
                                    <h2>Filtered totals</h2>
                                    <div
                                        className="insightsRateToggle"
                                        role="group"
                                        aria-label="Rate mode"
                                    >
                                        <button
                                            type="button"
                                            className={rateMode === 'totals' ? 'isActive' : ''}
                                            onClick={() => setRateMode('totals')}
                                        >
                                            Total
                                        </button>
                                        <button
                                            type="button"
                                            className={rateMode === 'perGame' ? 'isActive' : ''}
                                            onClick={() => setRateMode('perGame')}
                                        >
                                            Per game
                                        </button>
                                    </div>
                                </div>
                                <div className="insightsStatStrip insightsFilteredTotalsStrip">
                                    <div className="insightsStat">
                                        <b>{seasonRow.matches}</b>
                                        <i>Matches</i>
                                    </div>
                                    {pureGk || mapRole.showGk ? (
                                        <>
                                            <div className="insightsStat">
                                                <b>
                                                    {gkTotals
                                                        ? savePctText(
                                                              gkTotals.saves,
                                                              gkTotals.goalsAgainst
                                                          )
                                                        : '-'}
                                                </b>
                                                <i>Save %</i>
                                            </div>
                                            <div className="insightsStat">
                                                <b>{gkTotals ? show(gkTotals.sog) : '-'}</b>
                                                <i>SOG</i>
                                            </div>
                                            <div className="insightsStat">
                                                <b>{gkTotals ? show(gkTotals.saves) : '-'}</b>
                                                <i>Saves</i>
                                            </div>
                                            <div className="insightsStat">
                                                <b>
                                                    {gkTotals ? show(gkTotals.goalsAgainst) : '-'}
                                                </b>
                                                <i>GA</i>
                                            </div>
                                        </>
                                    ) : null}
                                    {pureField || mapRole.showShooter ? (
                                        <>
                                            <div className="insightsStat">
                                                <b>{show(seasonRow.shots.total.goals)}</b>
                                                <i>Goals</i>
                                            </div>
                                            <div className="insightsStat">
                                                <b>{show(seasonRow.shots.total.attempts)}</b>
                                                <i>Att</i>
                                            </div>
                                            <div className="insightsStat">
                                                <b>
                                                    {shotPctText(
                                                        seasonRow.shots.total.goals,
                                                        seasonRow.shots.total.attempts
                                                    )}
                                                </b>
                                                <i>Shooting</i>
                                            </div>
                                            <div className="insightsStat">
                                                <b>{show(seasonRow.assists)}</b>
                                                <i>Assists</i>
                                            </div>
                                            <div className="insightsStat">
                                                <b>{show(seasonRow.exclusionsDrawn)}</b>
                                                <i>ExD</i>
                                            </div>
                                            <div className="insightsStat">
                                                <b>{show(seasonRow.exclusionsCommitted)}</b>
                                                <i>ExC</i>
                                            </div>
                                            <div className="insightsStat">
                                                <b>
                                                    {show(
                                                        seasonRow.exclusionsDrawn -
                                                            seasonRow.exclusionsCommitted
                                                    )}
                                                </b>
                                                <i>Ex net</i>
                                            </div>
                                            <div className="insightsStat">
                                                <b>{show(seasonRow.turnovers)}</b>
                                                <i>TO</i>
                                            </div>
                                        </>
                                    ) : null}
                                </div>
                            </section>

                            {formRows.length > 0 || sampleHalves.length > 0 ? (
                                <div className="insightsTableCluster">
                                    {formRows.length > 0 ? (
                                        <section className="insightsPanel">
                                            <div className="insightsPanelHead">
                                                <h2>Form</h2>
                                                <span>Last 5 vs filtered sample</span>
                                            </div>
                                            <div className="insightsFormTableWrap">
                                                <table className="insightsPlayerTable insightsTableFit">
                                                    <thead>
                                                        <tr>
                                                            <th>Metric</th>
                                                            <th>Sample</th>
                                                            <th>Last 5</th>
                                                            <th>Change</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {formRows.map((row) => (
                                                            <tr key={row.label}>
                                                                <td>{row.label}</td>
                                                                <td>
                                                                    {formatFormValue(
                                                                        row.season,
                                                                        row.label
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {formatFormValue(
                                                                        row.recent,
                                                                        row.label
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <span
                                                                        className={`insightsFormDelta ${formDeltaClass(
                                                                            row
                                                                        )}`}
                                                                    >
                                                                        {formatFormDelta(row)}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </section>
                                    ) : null}

                                    {sampleHalves.length > 0 ? (
                                        <section className="insightsPanel">
                                            <div className="insightsPanelHead">
                                                <h2>Sample halves</h2>
                                                <span>
                                                    Older half vs newer half (per match avg)
                                                </span>
                                            </div>
                                            <table className="insightsPlayerTable insightsTableFit">
                                                <thead>
                                                    <tr>
                                                        <th>Metric</th>
                                                        <th>Older</th>
                                                        <th>Newer</th>
                                                        <th>Δ</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sampleHalves.map(
                                                        (row: SampleHalfComparison) => {
                                                            const up = row.lowerIsBetter
                                                                ? row.delta < 0
                                                                : row.delta > 0;
                                                            const down = row.lowerIsBetter
                                                                ? row.delta > 0
                                                                : row.delta < 0;
                                                            const cls = up
                                                                ? 'isUp'
                                                                : down
                                                                ? 'isDown'
                                                                : 'isFlat';
                                                            return (
                                                                <tr key={row.label}>
                                                                    <td>{row.label}</td>
                                                                    <td>{row.older.toFixed(1)}</td>
                                                                    <td>{row.newer.toFixed(1)}</td>
                                                                    <td>
                                                                        <span
                                                                            className={`insightsFormDelta ${cls}`}
                                                                        >
                                                                            {row.delta > 0
                                                                                ? '+'
                                                                                : ''}
                                                                            {row.delta.toFixed(1)}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        }
                                                    )}
                                                </tbody>
                                            </table>
                                        </section>
                                    ) : null}
                                </div>
                            ) : null}

                            {winLoss.length > 1 || venueSplits.length > 1 ? (
                                <div className="insightsTableCluster">
                                    {winLoss.length > 1 ? (
                                        <section className="insightsPanel">
                                            <div className="insightsPanelHead">
                                                <h2>Win / loss splits</h2>
                                                <span>Per-match lines by result</span>
                                            </div>
                                            <table className="insightsPlayerTable insightsTableFit">
                                                <thead>
                                                    <tr>
                                                        <th>Result</th>
                                                        <th>Mp</th>
                                                        <th>G</th>
                                                        <th>Sh</th>
                                                        <th>As</th>
                                                        <th>ExD</th>
                                                        <th>TO</th>
                                                        {mapRole.showGk ? <th>Sv</th> : null}
                                                        {mapRole.showGk ? <th>SOG</th> : null}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {winLoss.map((row: ResultSplitRow) => (
                                                        <tr key={row.label}>
                                                            <td>{row.label}</td>
                                                            <td>{row.matches}</td>
                                                            <td>{row.goals}</td>
                                                            <td>{row.shots}</td>
                                                            <td>{row.assists}</td>
                                                            <td>{row.exclusionsDrawn}</td>
                                                            <td>{row.turnovers}</td>
                                                            {mapRole.showGk ? (
                                                                <td>{row.saves}</td>
                                                            ) : null}
                                                            {mapRole.showGk ? (
                                                                <td>{row.sog}</td>
                                                            ) : null}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </section>
                                    ) : null}

                                    {venueSplits.length > 1 ? (
                                        <section className="insightsPanel">
                                            <div className="insightsPanelHead">
                                                <h2>Home / away</h2>
                                                <span>Per-match lines by venue</span>
                                            </div>
                                            <table className="insightsPlayerTable insightsTableFit">
                                                <thead>
                                                    <tr>
                                                        <th>Venue</th>
                                                        <th>Mp</th>
                                                        <th>G</th>
                                                        <th>Sh</th>
                                                        <th>As</th>
                                                        <th>ExD</th>
                                                        <th>TO</th>
                                                        {mapRole.showGk ? <th>Sv</th> : null}
                                                        {mapRole.showGk ? <th>SOG</th> : null}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {venueSplits.map((row: ResultSplitRow) => (
                                                        <tr key={row.label}>
                                                            <td>{row.label}</td>
                                                            <td>{row.matches}</td>
                                                            <td>{row.goals}</td>
                                                            <td>{row.shots}</td>
                                                            <td>{row.assists}</td>
                                                            <td>{row.exclusionsDrawn}</td>
                                                            <td>{row.turnovers}</td>
                                                            {mapRole.showGk ? (
                                                                <td>{row.saves}</td>
                                                            ) : null}
                                                            {mapRole.showGk ? (
                                                                <td>{row.sog}</td>
                                                            ) : null}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </section>
                                    ) : null}
                                </div>
                            ) : null}

                            {assistRecord || (foulTrouble && foulTrouble.matchesWithOne > 0) ? (
                                <div className="insightsTableCluster">
                                    {assistRecord ? (
                                        <section className="insightsPanel">
                                            <div className="insightsPanelHead">
                                                <h2>Assist threshold</h2>
                                                <span>
                                                    When As ≥ {assistRecord.threshold} · team result
                                                </span>
                                            </div>
                                            <div className="insightsStatStrip">
                                                <div className="insightsStat">
                                                    <b>{assistRecord.games}</b>
                                                    <i>Games</i>
                                                </div>
                                                <div className="insightsStat">
                                                    <b>
                                                        {assistRecord.wins}-{assistRecord.draws}-
                                                        {assistRecord.losses}
                                                    </b>
                                                    <i>W-D-L</i>
                                                </div>
                                            </div>
                                        </section>
                                    ) : null}

                                    {foulTrouble && foulTrouble.matchesWithOne > 0 ? (
                                        <section className="insightsPanel">
                                            <div className="insightsPanelHead">
                                                <h2>Foul trouble</h2>
                                                <span>Countdown clock at 1st / 2nd exclusion</span>
                                            </div>
                                            <div className="insightsStatStrip">
                                                <div className="insightsStat">
                                                    <b>{foulTrouble.matchesWithOne}</b>
                                                    <i>Matches with Ex</i>
                                                </div>
                                                <div className="insightsStat">
                                                    <b>{foulTrouble.matchesWithTwoPlus}</b>
                                                    <i>With 2+</i>
                                                </div>
                                                <div className="insightsStat">
                                                    <b>
                                                        {foulTrouble.avgFirstMinute == null
                                                            ? '-'
                                                            : foulTrouble.avgFirstMinute.toFixed(1)}
                                                    </b>
                                                    <i>Avg 1st ′ left</i>
                                                </div>
                                                <div className="insightsStat">
                                                    <b>
                                                        {foulTrouble.avgSecondMinute == null
                                                            ? '-'
                                                            : foulTrouble.avgSecondMinute.toFixed(
                                                                  1
                                                              )}
                                                    </b>
                                                    <i>Avg 2nd ′ left</i>
                                                </div>
                                            </div>
                                        </section>
                                    ) : null}
                                </div>
                            ) : null}

                            {playerGameState &&
                            (playerGameState.leading.shots > 0 ||
                                playerGameState.tied.shots > 0 ||
                                playerGameState.trailing.shots > 0 ||
                                playerGameState.clutch.shots > 0) ? (
                                <section className="insightsPanel">
                                    <div className="insightsPanelHead">
                                        <h2>Game state</h2>
                                        <span>
                                            Shots by score · clutch = last 5′ &amp; within 2
                                        </span>
                                    </div>
                                    <table className="insightsPlayerTable insightsTableFit">
                                        <thead>
                                            <tr>
                                                <th>State</th>
                                                <th>Goals</th>
                                                <th>Shots</th>
                                                <th>%</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(
                                                [
                                                    ['Leading', playerGameState.leading],
                                                    ['Tied', playerGameState.tied],
                                                    ['Trailing', playerGameState.trailing],
                                                    ['Clutch', playerGameState.clutch],
                                                ] as const
                                            ).map(([label, bucket]) => (
                                                <tr key={label}>
                                                    <td>{label}</td>
                                                    <td>{bucket.goals}</td>
                                                    <td>{bucket.shots}</td>
                                                    <td>
                                                        {shootPct(bucket) == null
                                                            ? '-'
                                                            : `${shootPct(bucket)}%`}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </section>
                            ) : null}

                            {matchLines.length > 0 ? (
                                <section className="insightsPanel">
                                    <div className="insightsPanelHead">
                                        <h2>Trend</h2>
                                        <div className="insightsRateToggle" role="group">
                                            {trendOptions.map((opt) => (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    className={
                                                        trendMetric === opt.value ? 'isActive' : ''
                                                    }
                                                    onClick={() => setTrendMetric(opt.value)}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <InsightsPlayerTrendChart
                                        points={trendSeries}
                                        valueLabel={
                                            trendOptions.find((opt) => opt.value === trendMetric)
                                                ?.label
                                        }
                                        percentScale={
                                            trendMetric === 'shootingPct' ||
                                            trendMetric === 'savePct'
                                        }
                                    />
                                    {consistency ? (
                                        <div className="insightsConsistencyStrip">
                                            <div className="insightsStat">
                                                <b>{consistency.average.toFixed(1)}</b>
                                                <i>Avg</i>
                                            </div>
                                            <div className="insightsStat">
                                                <b>{consistency.median.toFixed(1)}</b>
                                                <i>Median</i>
                                            </div>
                                            <div className="insightsStat">
                                                <b>{consistency.stdev.toFixed(1)}</b>
                                                <i>StDev</i>
                                            </div>
                                            <div className="insightsStat">
                                                <b>{consistency.best}</b>
                                                <i>Best</i>
                                            </div>
                                            <div className="insightsStat">
                                                <b>{consistency.worst}</b>
                                                <i>Worst</i>
                                            </div>
                                            <div className="insightsStat">
                                                <b>
                                                    {consistency.aboveAverage}/
                                                    {consistency.belowAverage}
                                                </b>
                                                <i>Above/below</i>
                                            </div>
                                        </div>
                                    ) : null}
                                </section>
                            ) : null}

                            {mapRole.showGk && (
                                <InsightsGkPlayerMaps
                                    bundles={filteredBundles}
                                    ourTeamIds={teamIds}
                                    playerId={playerId}
                                    hideScopeFilters
                                />
                            )}

                            {mapRole.showShooter && (
                                <InsightsPlayerShotMaps
                                    bundles={filteredBundles}
                                    ourTeamIds={teamIds}
                                    playerId={playerId}
                                    hideScopeFilters
                                />
                            )}

                            <div className="insightsPanelHead insightsProfileSectionHead">
                                <h2>Match by match</h2>
                            </div>
                            {(pureField || mapRole.showShooter) && (
                                <TwMatchScorersBoard lines={matchLines} />
                            )}
                            {mapRole.showGk && <TwMatchSavesBoard lines={matchSavesLines} />}
                            {!pureGk && <TwMatchSupportExclusionsBoard lines={matchLines} />}
                            {pureGk && matchSavesLines.length === 0 && matchLines.length > 0 ? (
                                <div className="insightsEmpty">
                                    Appearances found, but no attributed saves / SOG in the filtered
                                    set.
                                </div>
                            ) : null}
                        </>
                    )}
                </Spin>
            </div>
        </div>
    );
};

export default InsightsPlayer;
